import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/v1/ui/chart';
import { cn } from '@/lib/utils';
import { useState, useMemo, useRef } from 'react';
import {
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceArea,
  ResponsiveContainer,
  Bar,
  BarChart,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';

export type DataPoint<T extends string> = Record<T, number | null> & {
  date: string;
};

type DatedDataPoint = { date: string };

export const getNextActiveLabel = (
  activeLabel: string,
  data: DatedDataPoint[],
) => {
  const currentIndex = data.findIndex((d) => d.date === activeLabel);
  if (currentIndex === -1) {
    return null;
  }

  if (data.length === 1) {
    return activeLabel;
  }

  if (currentIndex === data.length - 1) {
    return activeLabel;
  }

  return data[currentIndex + 1]?.date || activeLabel;
};

export const getPrevActiveLabel = (
  activeLabel: string,
  data: DatedDataPoint[],
) => {
  const currentIndex = data.findIndex((d) => d.date === activeLabel);
  if (currentIndex === -1) {
    return activeLabel;
  }

  if (data.length === 1) {
    return activeLabel;
  }

  if (currentIndex === 0) {
    return activeLabel;
  }

  return data[currentIndex - 1]?.date || activeLabel;
};

type ZoomableChartProps<T extends string> = {
  data: DataPoint<T>[];
  colors?: Record<string, string>;
  zoom?: (startTime: string, endTime: string) => void;
  showYAxis?: boolean;
  kind: 'bar' | 'line' | 'area';
  className?: string;
  valueFormatter?: (value: number) => string;
  ariaLabel?: string;
};

export function ZoomableChart<T extends string>({
  data,
  colors,
  zoom,
  showYAxis = true,
  kind = 'bar',
  className,
  valueFormatter,
  ariaLabel,
}: ZoomableChartProps<T>) {
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);
  const [actualRefAreaLeft, setActualRefAreaLeft] = useState<string | null>(
    null,
  );
  const [actualRefAreaRight, setActualRefAreaRight] = useState<string | null>(
    null,
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const dataKeys = useMemo(
    () =>
      [
        ...new Set(
          data.flatMap((point) =>
            Object.keys(point).filter((key) => key !== 'date'),
          ),
        ),
      ] as T[],
    [data],
  );
  const chartConfig = useMemo<ChartConfig>(() => {
    return dataKeys.reduce<ChartConfig>((acc, key, index) => {
      let color = `hsl(${(index * 360) / dataKeys.length}, 70%, 50%)`;

      if (colors && colors[key]) {
        color = colors[key];
      }

      if (index < 5) {
        color = `hsl(var(--chart-${index + 1}))`;
      }

      acc[key] = {
        label: key,
        color: colors?.[key] || color,
      };
      return acc;
    }, {});
  }, [colors, dataKeys]);
  const summaries = useMemo(
    () =>
      dataKeys.map((key) => {
        const values = data.flatMap((point) => {
          const value = point[key];
          return typeof value === 'number' ? [value] : [];
        });
        return {
          key,
          latest: values.at(-1) ?? null,
          minimum: values.length === 0 ? null : Math.min(...values),
          maximum: values.length === 0 ? null : Math.max(...values),
        };
      }),
    [data, dataKeys],
  );

  if (data.length === 0) {
    return <div className={cn('h-[200px] min-h-[200px] w-full', className)} />;
  }

  const handleMouseDown = (e: any) => {
    if (data.length > 1 && e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
      setActualRefAreaLeft(getPrevActiveLabel(e.activeLabel, data));
      setIsSelecting(true);
    }
  };

  const handleMouseMove = (e: any) => {
    if (isSelecting && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
      setActualRefAreaRight(getNextActiveLabel(e.activeLabel, data));
    }
  };

  const handleMouseUp = () => {
    if (actualRefAreaLeft && actualRefAreaRight) {
      const [left, right] = [actualRefAreaLeft, actualRefAreaRight].sort();
      zoom?.(left, right);
    }
    setRefAreaLeft(null);
    setActualRefAreaLeft(null);
    setRefAreaRight(null);
    setActualRefAreaRight(null);
    setIsSelecting(false);
  };

  const minDate = new Date(
    Math.min(...data.map((d) => new Date(d.date).getTime())),
  );
  const maxDate = new Date(
    Math.max(...data.map((d) => new Date(d.date).getTime())),
  );

  const formatXAxis = (tickItem: string) => {
    const date = new Date(tickItem);
    const timeDiff = maxDate.getTime() - minDate.getTime();
    const oneDay = 24 * 60 * 60 * 1000;
    const sevenDays = 7 * oneDay;

    if (timeDiff > sevenDays) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } else if (timeDiff > oneDay) {
      return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  const displayValue = (value: number | null) => {
    if (value === null) {
      return '—';
    }
    return valueFormatter ? valueFormatter(value) : value.toLocaleString();
  };

  return (
    <>
      <ChartContainer
        config={chartConfig}
        role="img"
        aria-label={ariaLabel ?? 'Time series chart'}
        className={cn('h-[200px] min-h-[200px] w-full', className)}
      >
        <div className="h-full" ref={chartRef} style={{ touchAction: 'pan-y' }}>
          {getChildChart(kind, {
            data,
            showYAxis,
            formatXAxis,
            handleMouseDown,
            handleMouseMove,
            handleMouseUp,
            refAreaLeft,
            refAreaRight,
            chartConfig,
            dataKeys,
            valueFormatter,
          })}
        </div>
      </ChartContainer>
      <table className="sr-only">
        <caption>{ariaLabel ?? 'Chart'} data summary</caption>
        <thead>
          <tr>
            <th scope="col">Series</th>
            <th scope="col">Latest available</th>
            <th scope="col">Minimum</th>
            <th scope="col">Maximum</th>
          </tr>
        </thead>
        <tbody>
          {summaries.map((summary) => (
            <tr key={summary.key}>
              <th scope="row">{summary.key}</th>
              <td>{displayValue(summary.latest)}</td>
              <td>{displayValue(summary.minimum)}</td>
              <td>{displayValue(summary.maximum)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function getChildChart<T extends string>(
  kind: 'bar' | 'line' | 'area',
  props: ChildChartProps<T>,
) {
  switch (kind) {
    case 'bar':
      return <ChildBarChart {...props} />;
    case 'line':
      return <ChildLineChart {...props} />;
    case 'area':
      return <ChildAreaChart {...props} />;
  }
}

type ChildChartProps<T extends string> = {
  data: DataPoint<T>[];
  showYAxis?: boolean;
  formatXAxis: (tickItem: string) => string;
  handleMouseDown: (e: any) => void;
  handleMouseMove: (e: any) => void;
  handleMouseUp: () => void;
  refAreaLeft: string | null;
  refAreaRight: string | null;
  chartConfig: ChartConfig;
  dataKeys: T[];
  valueFormatter?: (value: number) => string;
};

function ChildBarChart<T extends string>({
  data,
  showYAxis = true,
  formatXAxis,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  refAreaLeft,
  refAreaRight,
  chartConfig,
  dataKeys,
  valueFormatter,
}: ChildChartProps<T>) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={formatXAxis}
          tickLine={false}
          axisLine={false}
          tickMargin={4}
          minTickGap={16}
          style={{ fontSize: '10px', userSelect: 'none' }}
        />
        {showYAxis && (
          <YAxis
            tickFormatter={valueFormatter}
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            style={{ fontSize: '10px', userSelect: 'none' }}
          />
        )}
        <ChartTooltip
          content={
            <ChartTooltipContent
              className="w-[150px] font-mono text-xs sm:w-[200px] sm:text-xs"
              labelFormatter={(value) => new Date(value).toLocaleString()}
              valueFormatter={(value) => valueFormatter?.(Number(value))}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        {dataKeys.map((key) => (
          <Bar
            key={key}
            type="monotone"
            dataKey={key}
            stroke={chartConfig[key].color}
            fillOpacity={1}
            fill={chartConfig[key].color}
            isAnimationActive={false}
          />
        ))}

        {refAreaLeft && refAreaRight && (
          <ReferenceArea
            x1={refAreaLeft}
            x2={refAreaRight}
            strokeOpacity={0.3}
            fill="hsl(var(--foreground))"
            fillOpacity={0.1}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

function ChildLineChart<T extends string>({
  data,
  showYAxis = true,
  formatXAxis,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  refAreaLeft,
  refAreaRight,
  chartConfig,
  dataKeys,
  valueFormatter,
}: ChildChartProps<T>) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={data}
        margin={{
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={formatXAxis}
          tickLine={false}
          axisLine={false}
          tickMargin={4}
          minTickGap={16}
          style={{ fontSize: '10px', userSelect: 'none' }}
        />
        {showYAxis && (
          <YAxis
            tickFormatter={valueFormatter}
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            style={{ fontSize: '10px', userSelect: 'none' }}
          />
        )}
        <ChartTooltip
          content={
            <ChartTooltipContent
              className="w-[150px] font-mono text-xs sm:w-[200px] sm:text-xs"
              labelFormatter={(value) => new Date(value).toLocaleString()}
              valueFormatter={(value) => valueFormatter?.(Number(value))}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        {dataKeys.map((key) => {
          return (
            <Line
              key={key}
              type="monotone"
              dot={false}
              dataKey={key}
              stroke={chartConfig[key].color}
              fillOpacity={1}
              fill={chartConfig[key].color}
              isAnimationActive={false}
            />
          );
        })}

        {refAreaLeft && refAreaRight && (
          <ReferenceArea
            x1={refAreaLeft}
            x2={refAreaRight}
            strokeOpacity={0.3}
            fill="hsl(var(--foreground))"
            fillOpacity={0.1}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

function ChildAreaChart<T extends string>({
  data,
  showYAxis = true,
  formatXAxis,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  refAreaLeft,
  refAreaRight,
  chartConfig,
  dataKeys,
  valueFormatter,
}: ChildChartProps<T>) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <XAxis
          dataKey="date"
          tickFormatter={formatXAxis}
          tickLine={false}
          axisLine={false}
          tickMargin={4}
          minTickGap={48}
          style={{ fontSize: '10px', userSelect: 'none' }}
        />
        {showYAxis && (
          <YAxis
            tickFormatter={valueFormatter}
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            style={{ fontSize: '10px', userSelect: 'none' }}
          />
        )}
        <ChartTooltip
          content={
            <ChartTooltipContent
              className="w-[150px] font-mono text-xs sm:w-[200px] sm:text-xs"
              labelFormatter={(value) => new Date(value).toLocaleString()}
              valueFormatter={(value) => valueFormatter?.(Number(value))}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        {dataKeys.map((key) => {
          return (
            <Area
              key={key}
              type="monotone"
              dot={false}
              dataKey={key}
              stroke={chartConfig[key].color}
              fillOpacity={0.6}
              fill={chartConfig[key].color}
              isAnimationActive={false}
              stackId="a"
            />
          );
        })}

        {refAreaLeft && refAreaRight && (
          <ReferenceArea
            x1={refAreaLeft}
            x2={refAreaRight}
            strokeOpacity={0.3}
            fill="hsl(var(--foreground))"
            fillOpacity={0.1}
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}
